<template>
  <OrderTestDialog 
    title="Choose Family Members to Add" 
    :width="970" 
    :is-show-dialog="isShowDialog" 
    @close="$emit('close')"
  >
    <p class="header-text pl-6">Gut Zoomer - Eligible for Family Discount</p>
    <div class="p-6 mt-5" style="background: #F9FBFD;">
      <div class="shipping-section p-4 mb-6">
        <p class="shipping-title mb-2">Shipping Address: {{ shippingAddress }}</p>
        <p class="shipping-description">
          Select patients living at the same address to receive 
          <span class="discount-text">20% off</span> on their tests. 
          <span class="underline italic font-medium">All orders will be shipped to this address.</span>
        </p>
      </div>

      <div class="grid grid-cols-3 gap-5 max-h-64 overflow-y-auto">
        <PatientCard 
          v-for="patient in familyMembers"
          :key="patient.id"
          :id="patient.id"
          :name="patient.name" 
          :gender="patient.gender" 
          :birth-date="patient.birthDate"
          :selected="patient.isSelected"
          @click="handleClickCard(patient.id)"
        />
        <PatientNotListedCard @click="handleSearchOrCreate" />
      </div>

      
    </div>
    <!-- Added Patients Section -->
    <div v-if="selectedPatients.length > 0" class="selected-patients-section mt-6">
      <p class="selected-patients-title">Added Patients for Family Discount Test:</p>
      <div class="flex flex-col gap-3 mt-5">
        <div 
          v-for="patient in selectedPatients" 
          :key="patient.id"
          class="selected-patient-item grid grid-cols-5 items-center"
        >
          <p>{{ patient.name }}</p>
          <p class="justify-self-end">{{ patient.gender }} <span v-if="patient.birthDate">|</span> {{ formatBirthdate(patient.birthDate) }}</p>
          <p class="justify-self-end">Gut Zoomer</p>
          <p class="justify-self-end"><span class="original-price">$500.00</span> <span class="discounted-price">$400.00</span> </p>
          <LoginButton class="justify-self-end" style="height: 16px;" text @click="removePatient(patient.id)">
            <span class="material-icons-outlined text-lg">clear</span>
          </LoginButton>
        </div>
      </div>
    </div>
      
    <Alert type="warning" class="mt-4">
      <p class="warning-text">
        Family discount orders are final and cannot be edited or canceled after submission.
      </p>
    </Alert>
    <Alert type="warning" class="mt-4">
      <p class="warning-text">
        eZ-Bill revenue won't apply to family bundle orders. Custom fees apply to base orders only.
      </p>
    </Alert>
    <template #footer>
      <div class="flex flex-row-reverse justify-between items-center mt-6">
        <LoginButton v-if="familyDiscountOrders.length > 0 || selectedPatients.length > 0" type="primary" class="w-36" @click="addToCart">
          {{ addToCartText }}
        </LoginButton>
        <LoginButton type="primary" plain class="w-36" @click="$emit('close')">
          Cancel
        </LoginButton>
      </div>
    </template>
  </OrderTestDialog>
</template>

<script>
import OrderTestDialog from "@/views/va-orderTest/components/OrderTestDialog.vue";
import PatientCard from "./PatientCard.vue";
import PatientNotListedCard from "./PatientNotListedCard.vue";
import Alert from "@/NewLibrary/Alert.vue";
import LoginButton from "@/views/login/components/LoginButton.vue";
import { mapState } from "vuex";
import moment from "moment";

export default {
  emits: ['close', 'searchOrCreatePatient'],
  components: {
    OrderTestDialog,
    PatientCard,
    PatientNotListedCard,
    Alert,
    LoginButton
  },
  props: {
    isShowDialog: {
      type: Boolean,
      default: false
    },
    shippingAddress: {
      type: String,
      default: ""
    }
  },
  computed: {
    ...mapState("orderTest/familyDiscount", ["familyMembers", "familyDiscountOrders", "isClickAddFamilyMember"]),
    selectedPatients() {
      return this.familyMembers.filter(p => p.isSelected);
    },
    addToCartText() {
      if (this.familyDiscountOrders.length > 0) {
        return "Update Cart";
      }
      return "Add to Cart";
    }
  },
  mounted() {
    if (this.isClickAddFamilyMember) {
      let selectedFamilyMemberIds = this.familyDiscountOrders.map(order => order.patient.id);
      this.familyMembers.forEach(p => p.isSelected = false);
      selectedFamilyMemberIds.forEach(id => {
        let patient = this.familyMembers.find(p => p.id === id);
        if (patient) {
          patient.isSelected = true;
        }
      });
      this.$store.dispatch("orderTest/familyDiscount/setFamilyMembers", this.familyMembers);
      this.$store.dispatch("orderTest/familyDiscount/setIsClickAddFamilyMember", false); // reset isClickAddFamilyMember after sync family members selection with family discount orders
    }
  },
  methods: {
    handleSearchOrCreate() {
      this.$emit('searchOrCreatePatient');
    },
    handleClickCard(patientId) {
      let patient = this.familyMembers.find(p => p.id === patientId);
      if (!patient) return;
      patient.isSelected = !patient.isSelected;
    },
    removePatient(patientId) {
      let patient = this.familyMembers.find(p => p.id === patientId);
      if (!patient) return;
      patient.isSelected = false;
    },
    addToCart() {
      this.$store.dispatch("orderTest/familyDiscount/setFamilyDiscountOrders");
      this.$emit("close");
    },
    formatBirthdate(birthdate) {
      return birthdate ? moment(birthdate).format("MMM-DD-YYYY") : "";
    }
  }
}
</script>

<style scoped>
.header-text {
  color: #161616;
  font-size: 18px;
  font-weight: 500;
  line-height: normal;
}

.shipping-section {
  border-radius: 5px;
  background: #E8F4FD;
}

.shipping-title {
  color: #333333;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
}

.shipping-description {
  color: #4A4A4A;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
}

.discount-text {
  color: #FF1010;
  font-weight: 500;
}

.warning-text {
  color: #333333;
  font-family: "IBM Plex Sans Hebrew";
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
}

/* Selected Patients Section */
.selected-patients-section {
  padding: 20px 24px;
  border-radius: 3px;
  border: 1px solid #E6E6E6;
}

.selected-patients-title {
  color: #161616;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.selected-patient-item {
  padding: 10px 20px;
  background: #F9FBFD;
  color: #333;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
}

.patient-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.patient-name {
  color: #333333;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.patient-details {
  color: #666666;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.test-info {
  flex: 1;
  text-align: center;
}

.test-name {
  color: #333333;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
}

.pricing-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex: 1;
}

.original-price {
  color: #979797;
  font-weight: 500;
  line-height: normal;
  text-decoration-line: line-through;
}

.discounted-price {
  color: #84BC3B;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}

.remove-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  transition: background-color 0.2s ease;
}

.remove-button:hover {
  background-color: #F5F5F5;
}
</style>
